
import { useLanguage } from "@/contexts/LanguageContext";
import { useState, useEffect } from "react";
import { BudgetCalculatorProps } from "./types";
import { calculateDiscount, generateDailyData } from "./utils";
import * as constants from "./constants";
import { getPricingConstants, getMediaOwnerFromLocationName } from "./media-owners";
import CampaignDetails from "./CampaignDetails";
import BudgetSummary from "./BudgetSummary";
import PerformanceMetrics from "./PerformanceMetrics";
import { secureStorage } from "@/utils/secureStorage";

const BudgetCalculator = ({ 
  devices, 
  duration, 
  mediaOwner = 'TableAds',
  selectedLocations = []
}: BudgetCalculatorProps) => {
  const { language } = useLanguage();

  // Calculate totals based on media owners in selected locations
  const calculateTotals = () => {
    let baseTotal = 0;
    let totalDiscountAmount = 0;
    let totalDevices = 0;
    
    // Group devices by media owner for proper discount calculation
    const mediaOwnerGroups: Record<string, number> = {};
    const mediaOwnerBaseTotals: Record<string, number> = {};
    const mediaOwnerDiscounts: Record<string, { discountRate: number; discountAmount: number }> = {};
    
    if (selectedLocations && selectedLocations.length > 0) {
      // Process selected locations
      selectedLocations.forEach(location => {
        const locationMediaOwner = location.mediaOwner || mediaOwner;
        const locationDevices = location.devices || 0;
        
        if (!mediaOwnerGroups[locationMediaOwner]) {
          mediaOwnerGroups[locationMediaOwner] = locationDevices;
        } else {
          mediaOwnerGroups[locationMediaOwner] += locationDevices;
        }
        
        totalDevices += locationDevices;
        
        const { costPerDevicePerDay } = getPricingConstants(locationMediaOwner);
        const locationBaseTotal = locationDevices * duration * costPerDevicePerDay;
        baseTotal += locationBaseTotal;
        
        if (!mediaOwnerBaseTotals[locationMediaOwner]) {
          mediaOwnerBaseTotals[locationMediaOwner] = locationBaseTotal;
        } else {
          mediaOwnerBaseTotals[locationMediaOwner] += locationBaseTotal;
        }
      });
    } else {
      // No locations selected, use the default mediaOwner and devices
      totalDevices = devices;
      mediaOwnerGroups[mediaOwner] = devices;
      const { costPerDevicePerDay } = getPricingConstants(mediaOwner);
      baseTotal = devices * duration * costPerDevicePerDay;
      mediaOwnerBaseTotals[mediaOwner] = baseTotal;
    }
    
    // Calculate discount for each media owner group separately
    Object.entries(mediaOwnerGroups).forEach(([owner, count]) => {
      const discountRate = calculateDiscount(count, owner);
      const ownerBaseTotal = mediaOwnerBaseTotals[owner] || 0;
      const discountAmount = ownerBaseTotal * discountRate;
      
      totalDiscountAmount += discountAmount;
      
      // Store discount information for each media owner
      mediaOwnerDiscounts[owner] = {
        discountRate,
        discountAmount
      };
    });
    
    const subtotal = baseTotal - totalDiscountAmount;
    const vatAmount = subtotal * constants.VAT_RATE;
    
    return {
      baseTotal,
      discountAmount: totalDiscountAmount,
      discountRate: baseTotal > 0 ? totalDiscountAmount / baseTotal : 0,
      subtotal,
      vatAmount,
      totalBudget: subtotal + vatAmount,
      totalDevices,
      mediaOwnerGroups,
      mediaOwnerDiscounts
    };
  };

  const {
    baseTotal,
    discountAmount,
    discountRate,
    subtotal,
    vatAmount,
    totalBudget,
    totalDevices,
    mediaOwnerGroups,
    mediaOwnerDiscounts
  } = calculateTotals();

  // Only calculate performance metrics for TableAds locations
  const tableAdsDevices = mediaOwnerGroups?.['TableAds'] || 0;
  const hasTableAdsLocations = tableAdsDevices > 0 || (selectedLocations.length === 0 && mediaOwner === 'TableAds');
  
  // Calculate performance metrics only for TableAds locations
  const tableAdsDeviceCount = hasTableAdsLocations ? (tableAdsDevices || totalDevices) : 0;
  const totalImpressions = tableAdsDeviceCount * constants.PEOPLE_PER_SCREEN * constants.HOURS_PER_DAY * duration;
  const expectedClicks = Math.round((totalImpressions / constants.PEOPLE_PER_SCREEN) * constants.AVG_CLICKS_PER_GROUP);
  const totalPlaytimes = constants.PLAYS_PER_HOUR * constants.HOURS_PER_DAY * tableAdsDeviceCount * duration;

  useEffect(() => {
    secureStorage.set('budgetCalculations', {
      baseTotal,
      discountRate,
      discountAmount,
      subtotal,
      vatAmount,
      totalBudget,
      totalImpressions,
      expectedClicks,
      totalPlaytimes,
      mediaOwner,
      totalDevices,
      mediaOwnerDiscounts
    }, 3600); // 1 hour expiry
  }, [baseTotal, discountRate, discountAmount, subtotal, vatAmount, totalBudget, totalImpressions, expectedClicks, totalPlaytimes, mediaOwner, totalDevices, mediaOwnerDiscounts]);

  const impressionsData = generateDailyData(totalImpressions, duration, 'impressions');
  const clicksData = generateDailyData(expectedClicks, duration, 'clicks');
  const playsData = generateDailyData(totalPlaytimes, duration, 'plays');

  return (
    <div className="w-full p-4 sm:p-6 bg-card dark:bg-card rounded-lg shadow-lg border border-border">
      <h3 className={`${language === 'ar' ? 'text-xl' : 'text-lg'} font-semibold mb-4 text-foreground`}>
        {language === 'ar' ? ':تقدير الميزانية' : 'Estimated Budget'}
      </h3>
      
      <CampaignDetails 
        devices={totalDevices}
        duration={duration}
        mediaOwner={mediaOwner}
        selectedLocations={selectedLocations}
      />

      <BudgetSummary 
        baseTotal={baseTotal}
        discountRate={discountRate}
        discountAmount={discountAmount}
        subtotal={subtotal}
        vatAmount={vatAmount}
        totalBudget={totalBudget}
        mediaOwnerDiscounts={mediaOwnerDiscounts}
      />

      <PerformanceMetrics 
        totalImpressions={totalImpressions}
        expectedClicks={expectedClicks}
        totalPlaytimes={totalPlaytimes}
        impressionsData={impressionsData}
        clicksData={clicksData}
        playsData={playsData}
        hasTableAdsLocations={hasTableAdsLocations}
      />
    </div>
  );
};

export default BudgetCalculator;
